/* eslint-disable @typescript-eslint/naming-convention */
import { Box } from '@lemonade-hq/cdk';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { NOOP } from '@lemonade-hq/ts-helpers';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { clsx } from 'clsx';
import type { ReactElement } from 'react';
import { Children, cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { Drawer as BaseDrawer } from 'vaul';
import type { ResponsiveValues } from '../../config/responsiveness';
import { useMobileKeyboardHeight } from '../../hooks/useMobileKeyboardHeight';
import { useResponsiveValue } from '../../hooks/useResponsiveValue';
import { targetContainsFocusableElements } from '../../utils/elements';

import * as styles from './Drawer.css';
import type { DrawerProps } from './types';

const DRAWER_CLOSE_ANIMATION_DELAY: Milliseconds = 300;
const DRAWER_OPEN_ANIMATION_DELAY: Milliseconds = 50;

const Root: React.FC<DrawerProps> = ({ dismissible, children, open, direction = 'bottom', ...props }) => {
  useEffect(() => {
    // This is here to fix weird bugs with vaul,
    // - for some reason radix react-dialog is setting the body's pointer events to none
    // - We need to remove position: 'fixed' because it prevents headlessui from displaying the list of items. This is fixed since version 0.9.3 of vaul
    // TODO: find out why vaul passes modal = true to radix dialog
    if (open) {
      const timeout = setTimeout(() => {
        document.body.style.pointerEvents = 'auto';
        document.body.style.position = '';
        document.body.style.height = '';
      }, DRAWER_OPEN_ANIMATION_DELAY);
      if (direction === 'top') {
        document.body.classList.add(styles.topDrawerIsOpen);
      }

      return () => {
        clearTimeout(timeout);
      };
    } else {
      const timeout = setTimeout(() => {
        document.body.classList.remove(styles.topDrawerIsOpen);
      }, DRAWER_CLOSE_ANIMATION_DELAY);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [open, direction]);

  return (
    <BaseDrawer.Root direction={direction} dismissible={dismissible} open={open} {...props}>
      {Children.map(children as ReactElement, (child: ReactElement) => cloneElement(child, { direction, open }))}
    </BaseDrawer.Root>
  );
};

const Trigger: React.FC<React.ComponentProps<typeof BaseDrawer.Trigger>> = props => (
  <BaseDrawer.Trigger {...props} asChild />
);

const DrawerCustomOverlay: React.FC<{ readonly open?: boolean }> = ({ open }) => {
  return <Box className={styles.overlay({ open })} />;
};

type ContentStyleProps = {
  readonly paddingBlockEnd?: ResponsiveValues<string> | string;
  readonly paddingBlockStart?: ResponsiveValues<string> | string;
};

export interface ContentProps extends React.ComponentProps<typeof BaseDrawer.Content>, ContentStyleProps {
  readonly direction?: DrawerProps['direction'];
  readonly fullscreen?: boolean;
  readonly fullViewport?: boolean;
  readonly open?: boolean;
  readonly isSnapped?: boolean;
  readonly onHandleClick?: (activeSnap: number | string | null) => void;
  readonly preventHandleOnClickCycle?: boolean;
  readonly showOverlay?: boolean;
  readonly useCustomOverlay?: boolean;
  readonly resetDefaultStyling?: boolean;
  readonly title: string;
  readonly description: string;
  readonly onAnimationEnd?: () => void;
}

const Content: React.FC<ContentProps> = ({
  className,
  children,
  showOverlay = true,
  useCustomOverlay = false,
  fullscreen = false,
  fullViewport = false,
  onHandleClick,
  isSnapped,
  paddingBlockEnd = styles.DEFAULT_DRAWER_BOTTOM_PADDING,
  paddingBlockStart = styles.DEFAULT_DRAWER_TOP_PADDING,
  open,
  direction,
  resetDefaultStyling = false,
  title,
  description,
  onAnimationEnd,
  ...props
}) => {
  // this is a workaround due to vaul not removing height after closing the drawer
  // https://github.com/emilkowalski/vaul/pull/539/files
  const [isNativeKeyboardOpen, setIsNativeKeyboardOpen] = useState(false);

  useMobileKeyboardHeight({ onChange: setIsNativeKeyboardOpen });
  const contentRef = useRef<HTMLDivElement>(null);

  const resolvedPaddingBlockEnd = useResponsiveValue(paddingBlockEnd);
  const resolvedPaddingBlockStart = useResponsiveValue(paddingBlockStart);

  useEffect(() => {
    if (isNativeKeyboardOpen) return;

    const raf = requestAnimationFrame(() => {
      contentRef.current?.style.removeProperty('height');
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [isNativeKeyboardOpen]);
  // end of workaround

  const onContentClick = useCallback(
    ({ target, currentTarget }: React.MouseEvent<HTMLDivElement>): void => {
      if (targetContainsFocusableElements(target, currentTarget)) {
        onHandleClick?.(null);
      }
    },
    [onHandleClick],
  );

  return (
    <BaseDrawer.Portal>
      {showOverlay && useCustomOverlay && <DrawerCustomOverlay open={open} />}
      {showOverlay && !useCustomOverlay && <BaseDrawer.Overlay className={styles.overlay()} />}
      <BaseDrawer.Content
        aria-label="drawer"
        className={clsx(
          styles.content({ direction, fullscreen, fullViewport, isSnapped, open, resetDefaultStyling }),
          className,
        )}
        ref={contentRef}
        style={{
          ...assignInlineVars({
            [styles.paddingBlockEndVar]: resolvedPaddingBlockEnd,
            [styles.paddingBlockStartVar]: resolvedPaddingBlockStart,
          }),
        }}
        {...props}
        onAnimationEnd={onAnimationEnd}
        onClick={onContentClick}
      >
        {
          /**
           * a workaround to prevent double click on handle from closing the drawer
           * waiting to https://github.com/emilkowalski/vaul/pull/363 to get merged
           */
          cloneElement(<BaseDrawer.Handle className={styles.handle} preventCycle={false} />, {
            onDoubleClick: onHandleClick ?? NOOP,
            onClick: onHandleClick ?? NOOP,
          })
        }

        <VisuallyHidden.Root>
          <BaseDrawer.Title>{title}</BaseDrawer.Title>
          <BaseDrawer.Description>{description}</BaseDrawer.Description>
        </VisuallyHidden.Root>
        {children}
      </BaseDrawer.Content>
    </BaseDrawer.Portal>
  );
};

export const Drawer = {
  Root,
  Trigger,
  Content,
};
